import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2, ArrowLeft, Stethoscope, Calendar, Play, CheckCircle,
  Video, Phone, Save, Mic, MicOff, Pause, Square, FileText,
  Sparkles, AlertTriangle, UserCircle, Pill, RefreshCw, Wand2,
  Clock, ChevronRight, TrendingUp, Volume2, BrainCircuit
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeIcons = { physical: Stethoscope, video: Video, phone: Phone, follow_up: Calendar };
const typeLabels = { physical: 'Physical', video: 'Video', phone: 'Phone', follow_up: 'Follow-up' };
const statusColors = {
  scheduled: 'bg-info/10 text-info border-info/20',
  in_progress: 'bg-warning/10 text-warning border-warning/20',
  completed: 'bg-success/10 text-success border-success/20',
  cancelled: 'bg-destructive/10 text-destructive border-destructive/20',
};
const statusLabels = { scheduled: 'Scheduled', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

const processingLabels = {
  transcribing: { text: 'Transcribing audio...', color: 'text-info' },
  translating: { text: 'Translating to English...', color: 'text-info' },
  summarizing: { text: 'Generating AI summary...', color: 'text-primary' },
  completed: { text: 'Processing complete', color: 'text-success' },
  failed: { text: 'Processing failed', color: 'text-destructive' },
};

function formatDate(d) { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }); }

function AudioRecorder({ consultationId, onUploadComplete, disabled }) {
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startTimer = () => {
    timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
  };
  const stopTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };
  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start(1000);
      mediaRecorderRef.current = mr;
      setRecording(true);
      setPaused(false);
      setElapsed(0);
      startTimer();
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setPaused(true);
      stopTimer();
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setPaused(false);
      startTimer();
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;
    stopTimer();
    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecording(false);
        setPaused(false);
        streamRef.current?.getTracks().forEach(t => t.stop());
        setUploading(true);
        try {
          const formData = new FormData();
          formData.append('file', blob, `recording_${Date.now()}.webm`);
          const { data } = await axios.post(`${API_URL}/api/consultations/${consultationId}/upload-audio`, formData, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Audio uploaded');
          onUploadComplete(data.audio_url);
        } catch (err) {
          toast.error('Failed to upload audio');
        } finally {
          setUploading(false);
        }
        resolve();
      };
      mediaRecorderRef.current.stop();
    });
  };

  useEffect(() => { return () => { stopTimer(); streamRef.current?.getTracks().forEach(t => t.stop()); }; }, []);

  if (uploading) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-muted/50" data-testid="audio-uploading">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <span className="text-sm">Uploading recording...</span>
      </div>
    );
  }

  if (!recording) {
    return (
      <Button onClick={startRecording} disabled={disabled} variant="outline" className="rounded-full gap-2" data-testid="start-recording-button">
        <Mic className="w-4 h-4 text-destructive" />
        Start Recording
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-destructive/5 border border-destructive/20" data-testid="recording-controls">
      <div className="w-3 h-3 rounded-full bg-destructive animate-pulse" />
      <span className="font-mono text-sm font-medium" data-testid="recording-timer">{formatTime(elapsed)}</span>
      {paused ? (
        <Button onClick={resumeRecording} size="sm" variant="outline" className="rounded-full gap-1" data-testid="resume-recording-button">
          <Play className="w-3 h-3" /> Resume
        </Button>
      ) : (
        <Button onClick={pauseRecording} size="sm" variant="outline" className="rounded-full gap-1" data-testid="pause-recording-button">
          <Pause className="w-3 h-3" /> Pause
        </Button>
      )}
      <Button onClick={stopRecording} size="sm" className="rounded-full gap-1 bg-destructive hover:bg-destructive/90" data-testid="stop-recording-button">
        <Square className="w-3 h-3" /> Stop & Save
      </Button>
    </div>
  );
}

function AISummaryPanel({ summary, isEditing, onEdit }) {
  if (!summary) return <p className="text-sm text-muted-foreground">No AI summary available yet.</p>;

  let parsed = null;
  try {
    let clean = summary.trim();
    if (clean.startsWith('```')) { clean = clean.split('\n', 1)[1] || clean.slice(3); clean = clean.replace(/```\s*$/, ''); }
    parsed = JSON.parse(clean);
  } catch {
    return (
      <div className="space-y-2">
        <p className="text-sm whitespace-pre-wrap">{summary}</p>
      </div>
    );
  }

  const fields = [
    { key: 'chief_complaint', label: 'Chief Complaint', icon: AlertTriangle },
    { key: 'duration', label: 'Duration', icon: Clock },
    { key: 'symptoms', label: 'Symptoms', isList: true },
    { key: 'past_treatments', label: 'Past Treatments', isList: true },
    { key: 'response_history', label: 'Response History' },
    { key: 'patient_concerns', label: 'Patient Concerns', isList: true },
    { key: 'red_flags', label: 'Red Flags', isList: true, isAlert: true },
    { key: 'patient_expectations', label: 'Patient Expectations' },
    { key: 'suggested_concern_tags', label: 'Suggested Tags', isTags: true },
    { key: 'examination_summary', label: 'Examination Summary' },
    { key: 'confidence_notes', label: 'Confidence Notes', isWarning: true },
  ];

  return (
    <div className="space-y-4" data-testid="ai-summary-structured">
      {fields.map(({ key, label, isList, isTags, isAlert, isWarning }) => {
        const val = parsed[key];
        if (!val || (Array.isArray(val) && val.length === 0) || val === 'Not discussed') return null;
        return (
          <div key={key} className={`${isAlert ? 'p-3 rounded-xl bg-destructive/5 border border-destructive/20' : ''}`}>
            <p className={`text-xs font-medium uppercase tracking-wider mb-1 ${isAlert ? 'text-destructive' : isWarning ? 'text-warning' : 'text-muted-foreground'}`}>
              {label}
            </p>
            {isList ? (
              <ul className="space-y-1">
                {val.map((item, i) => <li key={i} className="text-sm flex items-start gap-2"><span className="text-muted-foreground mt-1">·</span>{item}</li>)}
              </ul>
            ) : isTags ? (
              <div className="flex flex-wrap gap-1">
                {val.map((tag, i) => <Badge key={i} variant="secondary" className="text-xs capitalize">{tag}</Badge>)}
              </div>
            ) : (
              <p className="text-sm">{val}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ConsultationDetailPage() {
  const { consultationId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [processing, setProcessing] = useState(false);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [prescriptionDraft, setPrescriptionDraft] = useState(null);
  const pollingRef = useRef(null);

  const [formData, setFormData] = useState({
    notes: '', chief_complaint: '', examination_findings: '',
    diagnosis: '', treatment_plan: '', transcript: '', ai_summary: '',
  });

  // Patient history
  const [patientHistory, setPatientHistory] = useState({ prescriptions: [], followUps: [] });

  const fetchConsultation = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/consultations/${consultationId}`, { withCredentials: true });
      setConsultation(data);
      setFormData({
        notes: data.notes || '', chief_complaint: data.chief_complaint || '',
        examination_findings: data.examination_findings || '', diagnosis: data.diagnosis || '',
        treatment_plan: data.treatment_plan || '', transcript: data.transcript || '',
        ai_summary: data.ai_summary || '',
      });
      // Fetch patient history
      if (data.patient_id) {
        const [prescRes, fuRes] = await Promise.all([
          axios.get(`${API_URL}/api/prescriptions?patient_id=${data.patient_id}`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/follow-ups?patient_id=${data.patient_id}`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setPatientHistory({ prescriptions: prescRes.data, followUps: fuRes.data });
      }
    } catch (error) {
      toast.error('Failed to fetch consultation');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [consultationId, navigate]);

  useEffect(() => { fetchConsultation(); }, [fetchConsultation]);

  // Poll processing status
  useEffect(() => {
    const status = consultation?.processing_status;
    if (status && status !== 'completed' && status !== 'failed') {
      pollingRef.current = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API_URL}/api/consultations/${consultationId}/processing-status`, { withCredentials: true });
          if (data.processing_status === 'completed' || data.processing_status === 'failed') {
            clearInterval(pollingRef.current);
            fetchConsultation();
          }
          setConsultation(prev => prev ? { ...prev, processing_status: data.processing_status, processing_error: data.processing_error } : prev);
        } catch { /* ignore */ }
      }, 3000);
    }
    return () => clearInterval(pollingRef.current);
  }, [consultation?.processing_status, consultationId, fetchConsultation]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/consultations/${consultationId}`, formData, { withCredentials: true });
      toast.success('Consultation saved');
      fetchConsultation();
    } catch (error) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  const handleStart = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/consultations/${consultationId}/start`, {}, { withCredentials: true });
      toast.success('Consultation started');
      fetchConsultation();
    } catch (error) {
      toast.error('Failed to start');
    } finally { setSaving(false); }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/consultations/${consultationId}`, formData, { withCredentials: true });
      await axios.patch(`${API_URL}/api/consultations/${consultationId}/complete`, {}, { withCredentials: true });
      toast.success('Consultation completed');
      fetchConsultation();
    } catch (error) {
      toast.error('Failed to complete');
    } finally { setSaving(false); }
  };

  const handleProcessRecording = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/consultations/${consultationId}/process-recording`, {}, { withCredentials: true });
      toast.success('Processing started — transcript and summary will appear automatically');
      fetchConsultation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start processing');
    } finally { setProcessing(false); }
  };

  const handleGenerateDraft = async () => {
    setGeneratingDraft(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/consultations/${consultationId}/generate-prescription-draft`, {}, { withCredentials: true });
      setPrescriptionDraft(data);
      toast.success('Prescription draft generated');
      setActiveTab('prescription');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate draft');
    } finally { setGeneratingDraft(false); }
  };

  const handleCreatePrescriptionFromDraft = async () => {
    if (!prescriptionDraft || !consultation) return;
    try {
      const payload = {
        patient_id: consultation.patient_id,
        consultation_id: consultationId,
        morning_routine: prescriptionDraft.morning_routine || { medicines: [], notes: '' },
        evening_routine: prescriptionDraft.evening_routine || { medicines: [], notes: '' },
        oral_medicines: prescriptionDraft.oral_medicines || { medicines: [], notes: '' },
        procedures: prescriptionDraft.procedures || '',
        precautions: prescriptionDraft.precautions || '',
        follow_up_date: null,
        media_links: [],
        ai_draft: prescriptionDraft,
      };
      const { data } = await axios.post(`${API_URL}/api/prescriptions`, payload, { withCredentials: true });
      toast.success('Prescription created from draft — redirecting to editor');
      navigate(`/prescriptions/${data.id}/edit`);
    } catch (error) {
      toast.error('Failed to create prescription from draft');
    }
  };

  const handleAudioUploaded = (audioUrl) => {
    setConsultation(prev => prev ? { ...prev, audio_url: audioUrl } : prev);
    handleProcessRecording();
  };

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="consultation-detail-loading"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!consultation) return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Not found</p></div>;

  const TypeIcon = typeIcons[consultation.consultation_type] || Stethoscope;
  const isEditable = consultation.status !== 'completed' && consultation.status !== 'cancelled';
  const isProcessing = consultation.processing_status && consultation.processing_status !== 'completed' && consultation.processing_status !== 'failed';
  const pLabel = processingLabels[consultation.processing_status] || {};

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="consultation-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-medium tracking-tight">Consultation</h1>
            <Badge variant="outline" className={statusColors[consultation.status]}>{statusLabels[consultation.status]}</Badge>
          </div>
          <p className="text-muted-foreground text-sm">{typeLabels[consultation.consultation_type]} · {formatDate(consultation.consultation_date)} · {consultation.patient_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {consultation.status === 'scheduled' && (
            <Button onClick={handleStart} disabled={saving} className="bg-warning hover:bg-warning/90 text-warning-foreground rounded-full" data-testid="start-consultation-button">
              <Play className="w-4 h-4 mr-2" />Start
            </Button>
          )}
          {consultation.status === 'in_progress' && (
            <>
              <Button variant="outline" onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-consultation-button">
                <Save className="w-4 h-4 mr-2" />Save
              </Button>
              <Button onClick={handleComplete} disabled={saving} className="bg-success hover:bg-success/90 text-success-foreground rounded-full" data-testid="complete-consultation-button">
                <CheckCircle className="w-4 h-4 mr-2" />Complete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Processing Status Banner */}
      {consultation.processing_status && consultation.processing_status !== 'completed' && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${consultation.processing_status === 'failed' ? 'bg-destructive/5 border-destructive/20' : 'bg-info/5 border-info/20'}`} data-testid="processing-status-banner">
          {isProcessing ? <Loader2 className="w-5 h-5 animate-spin text-info" /> : consultation.processing_status === 'failed' ? <AlertTriangle className="w-5 h-5 text-destructive" /> : <CheckCircle className="w-5 h-5 text-success" />}
          <span className={`text-sm font-medium ${pLabel.color}`}>{pLabel.text}</span>
          {consultation.processing_status === 'failed' && consultation.processing_error && <span className="text-xs text-muted-foreground ml-2">{consultation.processing_error.slice(0, 100)}</span>}
          {consultation.processing_status === 'failed' && (
            <Button size="sm" variant="outline" onClick={handleProcessRecording} disabled={processing} className="ml-auto rounded-full" data-testid="retry-processing-button">
              <RefreshCw className="w-3 h-3 mr-1" />Retry
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* Left Panel: Patient + History */}
        <div className="lg:col-span-1 space-y-5">
          {/* Patient Info */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2"><UserCircle className="w-4 h-4 text-primary" />Patient</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {consultation.patient ? (
                <>
                  <div className="flex items-center gap-3 p-2 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted" onClick={() => navigate(`/patients/${consultation.patient_id}`)}>
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-heading font-semibold">{consultation.patient.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{consultation.patient.name}</p>
                      <p className="text-xs text-muted-foreground">{consultation.patient.age}y · {consultation.patient.gender} · {consultation.patient.skin_type}</p>
                    </div>
                  </div>
                  {consultation.patient.allergies?.length > 0 && (
                    <div className="p-2 rounded-lg bg-destructive/5 border border-destructive/20">
                      <div className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-destructive" /><span className="text-xs font-medium text-destructive">Allergies</span></div>
                      <p className="text-xs">{consultation.patient.allergies.join(', ')}</p>
                    </div>
                  )}
                </>
              ) : <p className="text-xs text-muted-foreground">Patient info unavailable</p>}
            </CardContent>
          </Card>

          {/* Audio Recording */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2"><Mic className="w-4 h-4 text-destructive" />Recording</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {consultation.audio_url && (
                <div className="space-y-2">
                  <audio controls className="w-full h-10" data-testid="audio-player">
                    <source src={`${API_URL}${consultation.audio_url}`} type="audio/webm" />
                  </audio>
                  {!consultation.transcript && consultation.processing_status !== 'transcribing' && consultation.processing_status !== 'summarizing' && (
                    <Button size="sm" variant="outline" onClick={handleProcessRecording} disabled={processing} className="w-full rounded-full" data-testid="process-recording-button">
                      {processing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Wand2 className="w-3 h-3 mr-1" />}
                      Process Recording
                    </Button>
                  )}
                </div>
              )}
              {isEditable && (
                <AudioRecorder consultationId={consultationId} onUploadComplete={handleAudioUploaded} disabled={!isEditable} />
              )}
            </CardContent>
          </Card>

          {/* Previous Prescriptions */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2"><Pill className="w-4 h-4 text-primary" />Rx History</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[200px]">
                {patientHistory.prescriptions.length === 0 ? <p className="text-xs text-muted-foreground">No previous prescriptions</p> : (
                  <div className="space-y-2">
                    {patientHistory.prescriptions.slice(0, 5).map(p => {
                      const count = (p.morning_routine?.medicines?.length || 0) + (p.evening_routine?.medicines?.length || 0) + (p.oral_medicines?.medicines?.length || 0);
                      return (
                        <div key={p.id} className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted text-xs" onClick={() => navigate(`/prescriptions/${p.id}/edit`)}>
                          <div className="flex justify-between"><span className="font-medium">{formatDate(p.created_at)}</span><span className="text-muted-foreground">{count} meds</span></div>
                          {p.follow_up_date && <p className="text-muted-foreground mt-1">Follow-up: {formatDate(p.follow_up_date)}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Follow-up Outcomes */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-success" />Follow-Ups</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="max-h-[200px]">
                {patientHistory.followUps.length === 0 ? <p className="text-xs text-muted-foreground">No follow-up records</p> : (
                  <div className="space-y-2">
                    {patientHistory.followUps.slice(0, 5).map(f => (
                      <div key={f.id} className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted text-xs" onClick={() => navigate(`/follow-ups/${f.id}`)}>
                        <div className="flex justify-between">
                          <span className="font-medium">{formatDate(f.created_at)}</span>
                          <Badge variant="outline" className="text-xs capitalize">{f.response}</Badge>
                        </div>
                        {f.side_effects && <p className="text-muted-foreground mt-1">SE: {f.side_effects.slice(0, 50)}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="border-border shadow-sm lg:col-span-3">
          <CardContent className="p-5">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 mb-5">
                <TabsTrigger value="notes" className="flex items-center gap-1.5 text-xs"><FileText className="w-3.5 h-3.5" />Notes</TabsTrigger>
                <TabsTrigger value="transcript" className="flex items-center gap-1.5 text-xs"><Mic className="w-3.5 h-3.5" />Transcript</TabsTrigger>
                <TabsTrigger value="summary" className="flex items-center gap-1.5 text-xs"><Sparkles className="w-3.5 h-3.5" />AI Summary</TabsTrigger>
                <TabsTrigger value="prescription" className="flex items-center gap-1.5 text-xs"><Pill className="w-3.5 h-3.5" />Rx Draft</TabsTrigger>
              </TabsList>

              {/* Clinical Notes Tab */}
              <TabsContent value="notes" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Chief Complaint</Label>
                    <Textarea value={formData.chief_complaint} onChange={(e) => setFormData(p => ({ ...p, chief_complaint: e.target.value }))}
                      placeholder="Main complaint..." className="min-h-[80px] rounded-xl text-sm" disabled={!isEditable} data-testid="chief-complaint-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Examination Findings</Label>
                    <Textarea value={formData.examination_findings} onChange={(e) => setFormData(p => ({ ...p, examination_findings: e.target.value }))}
                      placeholder="Physical examination..." className="min-h-[80px] rounded-xl text-sm" disabled={!isEditable} data-testid="examination-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Diagnosis</Label>
                    <Textarea value={formData.diagnosis} onChange={(e) => setFormData(p => ({ ...p, diagnosis: e.target.value }))}
                      placeholder="Clinical diagnosis..." className="min-h-[80px] rounded-xl text-sm" disabled={!isEditable} data-testid="diagnosis-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Treatment Plan</Label>
                    <Textarea value={formData.treatment_plan} onChange={(e) => setFormData(p => ({ ...p, treatment_plan: e.target.value }))}
                      placeholder="Treatment plan..." className="min-h-[80px] rounded-xl text-sm" disabled={!isEditable} data-testid="treatment-plan-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Additional Notes</Label>
                  <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                    placeholder="Additional notes..." className="min-h-[60px] rounded-xl text-sm" disabled={!isEditable} data-testid="notes-input" />
                </div>
              </TabsContent>

              {/* Transcript Tab */}
              <TabsContent value="transcript" className="space-y-4">
                {consultation.transcript_metadata && (
                  <div className="flex items-center gap-3 text-xs text-muted-foreground p-2 rounded-lg bg-muted/50" data-testid="transcript-metadata">
                    <Volume2 className="w-3.5 h-3.5" />
                    <span>Language: {consultation.transcript_metadata.original_language || 'Unknown'}</span>
                    {consultation.transcript_metadata.translated && <Badge variant="secondary" className="text-xs">Translated to English</Badge>}
                    <span>Transcribed: {consultation.transcript_metadata.transcribed_at ? formatDate(consultation.transcript_metadata.transcribed_at) : 'N/A'}</span>
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-xs">Transcript</Label>
                  <Textarea value={formData.transcript} onChange={(e) => setFormData(p => ({ ...p, transcript: e.target.value }))}
                    placeholder="Transcript will appear here after processing..." className="min-h-[400px] rounded-xl font-mono text-sm" disabled={!isEditable} data-testid="transcript-input" />
                </div>
              </TabsContent>

              {/* AI Summary Tab */}
              <TabsContent value="summary" className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">AI-generated structured clinical summary</p>
                  {consultation.transcript && !isProcessing && (
                    <Button size="sm" variant="outline" onClick={handleProcessRecording} disabled={processing} className="rounded-full" data-testid="regenerate-summary-button">
                      <RefreshCw className="w-3 h-3 mr-1" />Regenerate
                    </Button>
                  )}
                </div>
                <ScrollArea className="max-h-[500px]">
                  <AISummaryPanel summary={formData.ai_summary} />
                </ScrollArea>
                {isEditable && (
                  <div className="space-y-2 pt-4 border-t border-border">
                    <Label className="text-xs">Edit Raw Summary</Label>
                    <Textarea value={formData.ai_summary} onChange={(e) => setFormData(p => ({ ...p, ai_summary: e.target.value }))}
                      placeholder="Edit AI summary..." className="min-h-[150px] rounded-xl text-sm font-mono" data-testid="ai-summary-edit" />
                  </div>
                )}
              </TabsContent>

              {/* Prescription Draft Tab */}
              <TabsContent value="prescription" className="space-y-4">
                {!prescriptionDraft ? (
                  <div className="text-center py-8">
                    <Wand2 className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground mb-4">Generate an AI prescription draft from the transcript and summary</p>
                    <Button onClick={handleGenerateDraft} disabled={generatingDraft || (!consultation.transcript && !consultation.ai_summary)}
                      className="bg-primary hover:bg-primary/90 rounded-full" data-testid="generate-prescription-draft-button">
                      {generatingDraft ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                      Generate Prescription Draft
                    </Button>
                    {!consultation.transcript && !consultation.ai_summary && (
                      <p className="text-xs text-muted-foreground mt-2">Requires transcript or AI summary first</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="prescription-draft">
                    {prescriptionDraft.parse_error ? (
                      <div className="p-4 rounded-xl bg-muted/50"><p className="text-sm whitespace-pre-wrap font-mono">{prescriptionDraft.raw_response}</p></div>
                    ) : (
                      <>
                        {/* Learning Metadata Indicator */}
                        {prescriptionDraft._learning_metadata?.patterns_used && (
                          <div className="p-3 rounded-xl bg-violet-50 border border-violet-200" data-testid="learning-insights-badge">
                            <div className="flex items-center gap-2 mb-1">
                              <BrainCircuit className="w-4 h-4 text-violet-600" />
                              <p className="text-xs font-bold text-violet-700 uppercase tracking-wider">AI Learning Active</p>
                            </div>
                            <p className="text-xs text-violet-600">
                              Analyzed {prescriptionDraft._learning_metadata.total_overrides_analyzed} past override{prescriptionDraft._learning_metadata.total_overrides_analyzed !== 1 ? 's' : ''} to personalize this draft
                              {prescriptionDraft._learning_metadata.substitutions_applied > 0 && ` · ${prescriptionDraft._learning_metadata.substitutions_applied} substitution pattern${prescriptionDraft._learning_metadata.substitutions_applied !== 1 ? 's' : ''}`}
                              {prescriptionDraft._learning_metadata.additions_applied > 0 && ` · ${prescriptionDraft._learning_metadata.additions_applied} addition preference${prescriptionDraft._learning_metadata.additions_applied !== 1 ? 's' : ''}`}
                              {prescriptionDraft._learning_metadata.removals_applied > 0 && ` · ${prescriptionDraft._learning_metadata.removals_applied} removal pattern${prescriptionDraft._learning_metadata.removals_applied !== 1 ? 's' : ''}`}
                            </p>
                          </div>
                        )}
                        {/* Learning Applied List */}
                        {prescriptionDraft.learning_applied?.length > 0 && (
                          <div className="p-3 rounded-xl bg-violet-50/50 border border-violet-100">
                            <p className="text-xs font-medium text-violet-600 uppercase tracking-wider mb-2">Preferences Applied</p>
                            <div className="space-y-1">
                              {prescriptionDraft.learning_applied.map((item, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs text-violet-700">
                                  <CheckCircle className="w-3 h-3 mt-0.5 text-violet-500 flex-shrink-0" />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {prescriptionDraft.rationale && (
                          <div className="p-3 rounded-xl bg-primary/5 border border-primary/20">
                            <p className="text-xs font-medium text-primary uppercase tracking-wider mb-1">Rationale</p>
                            <p className="text-sm">{prescriptionDraft.rationale}</p>
                          </div>
                        )}
                        {(prescriptionDraft.allergy_warnings?.length > 0 || prescriptionDraft.stock_warnings?.length > 0) && (
                          <div className="space-y-2">
                            {prescriptionDraft.allergy_warnings?.map((w, i) => (
                              <div key={i} className="p-2 rounded-lg bg-destructive/5 border border-destructive/20 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-destructive" />{w}</div>
                            ))}
                            {prescriptionDraft.stock_warnings?.map((w, i) => (
                              <div key={i} className="p-2 rounded-lg bg-warning/5 border border-warning/20 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-warning" />{w}</div>
                            ))}
                          </div>
                        )}
                        {['morning_routine', 'evening_routine', 'oral_medicines'].map(section => {
                          const data = prescriptionDraft[section];
                          if (!data?.medicines?.length) return null;
                          const labels = { morning_routine: 'Morning Routine', evening_routine: 'Evening Routine', oral_medicines: 'Oral Medicines' };
                          return (
                            <div key={section}>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{labels[section]}</p>
                              {data.medicines.map((m, i) => (
                                <div key={i} className="p-3 rounded-xl bg-muted/50 mb-2">
                                  <p className="font-medium text-sm">{m.medicine_name}</p>
                                  <p className="text-xs text-muted-foreground">{[m.dosage, m.frequency, m.duration].filter(Boolean).join(' · ')}</p>
                                  {m.instructions && <p className="text-xs mt-1">{m.instructions}</p>}
                                </div>
                              ))}
                              {data.notes && <p className="text-xs text-muted-foreground italic mb-3">{data.notes}</p>}
                            </div>
                          );
                        })}
                        {prescriptionDraft.procedures && <div><p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Procedures</p><p className="text-sm">{prescriptionDraft.procedures}</p></div>}
                        {prescriptionDraft.precautions && <div><p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Precautions</p><p className="text-sm">{prescriptionDraft.precautions}</p></div>}
                        {prescriptionDraft.follow_up_date_suggestion && <div><p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Follow-up</p><p className="text-sm">{prescriptionDraft.follow_up_date_suggestion}</p></div>}
                      </>
                    )}
                    <div className="flex gap-2 pt-4 border-t border-border">
                      <Button onClick={handleCreatePrescriptionFromDraft} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="create-prescription-from-draft">
                        <Pill className="w-4 h-4 mr-2" />Create Prescription from Draft
                      </Button>
                      <Button onClick={handleGenerateDraft} disabled={generatingDraft} variant="outline" className="rounded-full" data-testid="regenerate-draft-button">
                        <RefreshCw className="w-4 h-4 mr-2" />{generatingDraft ? 'Generating...' : 'Regenerate'}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
